import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { CardNumberElement } from '@stripe/react-stripe-js';

import RatePlanView from 'views/Administration/RatePlan';
import PlanService from 'api/Plan';
import RateService from 'api/Rate';
import SettingsActions from 'store/reducers/Settings';
import { useFetch } from 'hooks';
import { handlePromise } from 'utils';
import PaymentService from 'api/Payment';
import AccountService from 'api/Account';
import UserService from 'api/User';
import AccountActions from 'store/reducers/Account';
import UserActions from 'store/reducers/User';

const RatePlan = ({
  account, accountPlanId, userEmail, updateAccountInfo, updateUser,
  toggleErrorAlert, toggleLoading, user,
}) => {
  const [dataSubmitted, setDataSubmitted] = useState(new Date().valueOf());
  const [accountLoading, setAccountLoading] = useState(false);

  // const [data, dataLoading] = useFetch( {
  //   initialState: { fee: [], noFee: [] },
  //   promise: () => PlanService.getPlans( { limit: -1, 'filters[active]': '1' } ),
  //   format: ( dataToFormat ) => ( {
  //     noFee: _.filter( dataToFormat.data, { label: 'NO_FEE' } ),
  //     fee: _.filter( dataToFormat.data, { label: 'FEE' } ),
  //   } ),
  //   toggleErrorAlert,
  //   deps: [dataSubmitted],
  // } );
  const [data, dataLoading] = useFetch({
    initialState: [],
    promise: () => PlanService.getPlans({ limit: -1, 'filters[active]': '1' }),
    format: (dataToFormat) => (dataToFormat.data),
    toggleErrorAlert,
    deps: [dataSubmitted],
  });

  const [accountData] = useFetch({
    initialState: {},
    promise: AccountService.getMyAccount,
    toggleErrorAlert,
  });

  const [rates] = useFetch({
    initialState: [],
    promise: () => RateService.getRates({ limit: -1 }),
    format: (dataToFormat) => (dataToFormat.data),
    toggleErrorAlert,
  });

  const submitForm = useCallback(async (paymentData, stripe, elements, callback) => {
    if (parseInt(paymentData.total, 10)) {
      toggleLoading(true);

      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement),
        billing_details: {
          email: userEmail,
          name: account.fiscalName || account.name,
          address: {
            city: account.city,
            country: account.country,
            postal_code: account.postalCode,
            line1: account.address,
          },
        },
      });

      if (!result) return 'apiError';
      if (result.error) {
        if (result.error.message) return result.error.message;
        return 'apiError';
      }

      const [errors, response, responseData] = await handlePromise(
        PaymentService.subscribePlan({
          email: userEmail,
          paymentMethod: result.paymentMethod.id,
          plan: paymentData.id,
          planType: paymentData.planType === 'year' ? 'annual' : 'monthly',
          type: 'plan',
          // withTax:((accountData && accountData.taxes) || (accountData && accountData.vat=="")) ? 1 : 0,
        }),
      );
      if (!response.ok) {
        toggleLoading(false);
        return errors;
      }

      if (responseData.status === 'requires_action') {
        const confirmResult = await stripe.confirmCardPayment(responseData.client_secret);

        if (confirmResult.error) {
          if (!confirmResult.error.payment_intent) {
            toggleLoading(false);
            return confirmResult.error.message || 'apiError';
          }

          const [updateErrors, updateResponse] = await handlePromise(
            PaymentService.updateSubscription(responseData.id, {
              data: JSON.stringify(confirmResult.error.payment_intent),
            }),
          );
          toggleLoading(false);
          if (confirmResult.error.message) return confirmResult.error.message;
          if (!updateResponse.ok) return updateErrors;
          return 'apiError';
        }
        const [updateErrors, updateResponse] = await handlePromise(
          PaymentService.updateSubscription(responseData.id, {
            data: JSON.stringify(confirmResult.paymentIntent),
          }),
        );
        toggleLoading(false);
        if (!updateResponse.ok) return updateErrors;
        return callback();
      }
      toggleLoading(false);
      return callback();
    }
  }, [account, userEmail, toggleLoading, accountData]);

  const reloadData = useCallback(() => {
    setDataSubmitted(new Date().valueOf());
  }, []);

  const reloadAccount = useCallback(async () => {
    setAccountLoading(true);
    const [, accountResponse, accountResponseData] = await handlePromise(
      AccountService.getMyAccount(),
    );
    if (!accountResponse.ok) return setAccountLoading(false);
    updateAccountInfo(accountResponseData);

    const [, response, responseData] = await handlePromise(UserService.getUser());
    setAccountLoading(false);
    if (response.ok) {
      updateUser(responseData);
    }
  }, [updateAccountInfo, updateUser]);

  const submitFormNoFee = useCallback(async (id) => {
    toggleLoading(true);

    const [errors, response] = await handlePromise(
      PaymentService.subscribePlanFee({
        plan: id,
        type: 'plan',
      }),
    );
    toggleLoading(false);
    if (!response.ok) return _.isString(errors) ? toggleErrorAlert(errors) : 'apiError';

    reloadData();
    reloadAccount();
  }, [reloadData, reloadAccount, toggleLoading, toggleErrorAlert]);

  return (
    <RatePlanView
      data={data}
      accountData={accountData}
      accountPlanId={accountPlanId}
      accountPlanType={account.planType}
      rates={rates}
      isLoading={dataLoading || accountLoading}
      onSubmit={submitForm}
      onSubmitFormNoFee={submitFormNoFee}
      onReload={reloadData}
      onReloadAccount={reloadAccount}
      userData={user}
    />
  );
};

const mapStateToProps = ({ account, user }) => ({
  accountPlanId: _.get(account, 'currentPlan.id'),
  userEmail: _.get(user, 'email'),
  account,
  user,
});

const mapDispatchToProps = ({
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
  updateAccountInfo: AccountActions.updateInfo,
  updateUser: UserActions.updateUser,
});

export default connect(mapStateToProps, mapDispatchToProps)(RatePlan);
